import { createApi } from '@reduxjs/toolkit/query/react';
import { get } from 'lodash-es';

import { RootStateType } from '@store/store';

import { TagTypesEnum } from '@enum/tag-types.enum';
import {
  ApplicationInputInterface,
  ApplicationInterface,
  ApplicationMetaInputInterface,
} from '@interface/application/application.interface';
import { ConsumerMetaInterface } from '@interface/consumer/consumer.interface';
import { ResponseDataInterface } from '@interface/logs/response-data.interface';
import { portalQueryAuth } from '@util/api/portal-query-auth';
import { isExist } from '@util/data/is-exist';

export const applicationApi = createApi({
  reducerPath: 'application-api',
  tagTypes: [TagTypesEnum.Application],
  baseQuery: portalQueryAuth('/applications'),
  endpoints: (build) => ({
    loadApplications: build.query<ResponseDataInterface<ApplicationInterface[]>, void>({
      query: () => `/`,
      providesTags: [TagTypesEnum.Application],
    }),
    addApplication: build.mutation<ResponseDataInterface<ApplicationInterface>, ApplicationInputInterface>({
      query: (credentials: ApplicationInputInterface) => ({
        url: '/',
        method: 'POST',
        body: credentials,
      }),
      invalidatesTags: (result: any, error: any) => (isExist(error) ? [] : [TagTypesEnum.Application]),
    }),
    loadApplication: build.query<ResponseDataInterface<ApplicationInterface>, string>({
      query: (id: string) => `/${id}`,
      providesTags: [TagTypesEnum.Application],
    }),
    getAccessToken: build.query<any, ApplicationMetaInputInterface>({
      queryFn: async (data: ApplicationMetaInputInterface, api: any, _extraOptions: any, fetch: any): Promise<any> => {
        const token: string = (api.getState() as RootStateType).auth.data.token;

        const result: any = await fetch({
          url: `${import.meta.env.VITE_AUTH_SERVICE_BASE_URL}`,
          method: 'POST',
          headers: {
            authorization: `Bearer ${token}`,
          },
          body: {
            ...data,
            scope: 'application',
          },
        });

        return {
          data: {
            accessToken: get(result.data as ResponseDataInterface<ConsumerMetaInterface>, ['data', 'accessToken']),
          },
        };
      },
    }),
    deleteApplication: build.mutation<ResponseDataInterface<ApplicationInterface>, { id: string; account: string }>({
      query: ({ id, account }: { id: string; account: string }) => ({
        url: `/${id}`,
        method: 'DELETE',
        body: { account },
      }),
      invalidatesTags: [TagTypesEnum.Application],
    }),
  }),
});
